/**
 * Information architecture of the Control Center (docs/specs/control-center-navigation.md,
 * revised 2026-09-29 in docs/handoff/ui-audit-2026-09-29.md): ten hubs, each a page with tabs
 * (and, for some tabs, sub-tabs). Every former list page is a tab or sub-tab here, and every
 * former URL redirects to it (`LEGACY_REDIRECTS` for old paths, `MOVED_TABS` for hub tabs that
 * moved to another hub).
 *
 * This file is plain data with no imports so the redirect test (tests/redirects.test.mjs) can
 * load it in Node. Labels live in the `nav` namespace (`hubs.<hub>.label`,
 * `hubs.<hub>.tabs.<tab>`, `hubs.<hub>.subs.<tab>.<sub>`); pages are wired in `hubPages.tsx`.
 *
 * URL shape: `<hub path>?tab=<tab>&sub=<sub>`. A page shown inside a tab keeps its own query
 * parameters next to these; a page's own `tab` parameter is stored as `pane` (see scopedParams).
 */

export type HubId = 'home' | 'domains' | 'customers' | 'sales' | 'products' | 'compliance' | 'ops' | 'settings' | 'backorder' | 'auctions';

/** Permission families: a tab is shown when the signed-in user holds any one of them. */
export const PERMS = {
  domain: ['domain.read'],
  customer: ['customer.manage', 'subject.read', 'dossier.read', 'domain.read'],
  subject: ['subject.read', 'dossier.read', 'customer.manage'],
  billing: ['billing.read', 'billing.adjust', 'pricing.manage'],
  pricing: ['pricing.manage', 'billing.read', 'tax.manage'],
  /** The product catalogue (ADR-026): hosting, cloud, email … plans, their prices and delivery. */
  catalog: ['catalog.read', 'catalog.manage', 'catalog.publish'],
  /** Sold services (ADR-026 §6): hosting, servers, e-mail … customers bought, their due dates and overdue state. */
  subscription: ['subscription.read'],
  auction: ['auction.read', 'auction.manage'],
  registry: ['registry.transaction.read', 'registry.reconcile', 'registry.credential.manage', 'ops.read'],
  compliance: ['compliance.read', 'regulatory_report.read', 'abuse.read', 'disclosure.read', 'escrow.read', 'dispute.manage'],
  /** Reporting to the authorities (VNNIC is the channel): permissions named by function (ADR-021). */
  vnnic: ['regulatory_report.read', 'regulatory_report.manage', 'compliance.read'],
  icann: ['compliance.read', 'disclosure.read', 'escrow.read', 'abuse.read'],
  cases: ['abuse.read', 'disclosure.read', 'dispute.manage', 'compliance.read', 'compliance.legal_unlock'],
  ops: ['ops.read'],
  integration: ['registry.credential.manage', 'reference.sync', 'admin.users.manage', 'ops.admin'],
  notify: ['admin.users.manage', 'customer.manage', 'ops.admin'],
  staff: ['admin.users.manage', 'audit.read'],
  apiKeys: ['api_credential.manage', 'admin.users.manage'],
  audit: ['audit.read'],
  /** Cài đặt › Kết nối › API báo cáo: the places that receive reporting data (Twenty CRM, …). */
  reporting: ['reporting_key.read', 'reporting_key.manage'],
} as const satisfies Record<string, readonly string[]>;

export interface HubSubDef {
  id: string;
  /** Former list URLs that now open this sub-tab. */
  legacy?: readonly string[];
  perms?: readonly string[];
  /** Also shown to customer users. */
  client?: boolean;
}

export interface HubTabDef extends HubSubDef {
  subs?: readonly HubSubDef[];
}

export interface HubDef {
  id: HubId;
  path: string;
  tabs: readonly HubTabDef[];
}

export const HUBS: readonly HubDef[] = [
  {
    id: 'home',
    path: '/',
    tabs: [
      // "Hồ sơ tên miền": dossiers to review before a domain activates (the default tab).
      { id: 'dossiers', perms: ['dossier.review', 'compliance.read', 'work_item.read'] },
      { id: 'queue', legacy: ['/compliance/work'] },
      { id: 'protected', legacy: ['/compliance/protected'], perms: PERMS.domain },
      { id: 'approvals', legacy: ['/admin/approvals'] },
      { id: 'deadlines', legacy: ['/compliance/deadlines'], perms: PERMS.compliance },
      { id: 'requests', legacy: ['/name-changes'], perms: PERMS.domain },
    ],
  },
  {
    id: 'domains',
    path: '/domains',
    tabs: [
      { id: 'list', perms: PERMS.domain, client: true },
      // "Tra cứu tên miền": availability across TLDs and WHOIS / RDAP records (staff).
      { id: 'lookup', perms: PERMS.domain },
      { id: 'transfers', legacy: ['/transfers'], perms: PERMS.domain, client: true },
      { id: 'registrant-changes', legacy: ['/registrant-changes'], perms: PERMS.domain, client: true },
      { id: 'contacts', legacy: ['/contacts'], perms: PERMS.domain, client: true },
    ],
  },
  {
    id: 'customers',
    path: '/customers',
    tabs: [
      { id: 'customers', perms: PERMS.customer, client: true },
      { id: 'subjects', legacy: ['/subjects'], perms: PERMS.subject, client: true },
      { id: 'dossiers', legacy: ['/dossiers'], perms: PERMS.subject, client: true },
      { id: 'documents', legacy: ['/documents'], perms: PERMS.subject, client: true },
    ],
  },
  {
    id: 'sales',
    path: '/sales',
    tabs: [
      { id: 'quotes', legacy: ['/quotes'], perms: PERMS.billing, client: true },
      { id: 'orders', legacy: ['/orders'], perms: PERMS.billing, client: true },
      { id: 'invoices', legacy: ['/invoices'], perms: PERMS.billing, client: true },
      { id: 'payments', legacy: ['/payments'], perms: PERMS.billing, client: true },
      // Giao dịch ngân hàng: what the bank feeds report on the receiving accounts (staff only).
      { id: 'bank-transactions', perms: PERMS.billing },
      { id: 'ledger', legacy: ['/ledger'], perms: PERMS.billing, client: true },
      // ADR-026 §6 "Dịch vụ": the services customers bought (list and a service's page).
      { id: 'subscriptions', perms: PERMS.subscription },
      { id: 'overview', legacy: ['/billing'], perms: PERMS.billing },
    ],
  },
  {
    // "TLD & giá bán": what we sell and at what price, in one place (owner 2026-09-29: "Bán tên
    // miền: TLD, giá, khuyến mãi, dịch vụ"). The TLD list moved here from the former registry hub.
    id: 'products',
    path: '/products',
    tabs: [
      { id: 'tlds', legacy: ['/registry/tlds'], perms: [...PERMS.registry, ...PERMS.pricing, 'registry.tld.manage'] },
      { id: 'pricing', legacy: ['/pricing'], perms: PERMS.pricing },
      { id: 'promotions', legacy: ['/promotions'], perms: PERMS.pricing },
      { id: 'services', legacy: ['/domain-services'], perms: PERMS.pricing },
      { id: 'tax', perms: PERMS.pricing },
      // "Tính thử báo giá" (reference 26): what a TLD's operations cost with their fees and add-ons.
      { id: 'quote', perms: PERMS.pricing },
      { id: 'catalog', perms: [...PERMS.registry, 'registry.tld.manage', 'integration.read'] },
      // ADR-026: Danh mục sản phẩm (groups, products, plans), Nhóm máy chủ, Vị trí, Tính thử dịch vụ,
      // Giá theo hạng (plan price rules of the tier and customer books) and Khuyến mãi dịch vụ (promotion scopes).
      {
        id: 'product-catalog',
        perms: PERMS.catalog,
        subs: [
          { id: 'products', perms: PERMS.catalog },
          { id: 'server-groups', perms: PERMS.catalog },
          { id: 'locations', perms: PERMS.catalog },
          { id: 'service-quote', perms: PERMS.catalog },
          { id: 'price-rules', perms: PERMS.catalog },
          { id: 'promotion-scopes', perms: PERMS.catalog },
        ],
      },
    ],
  },
  {
    // "Backorder" module (reference 58–63): names about to drop, who waits for them, the catch
    // attempts and the money held for them. The former "Đặt giữ" sub-tab of Tên miền.
    id: 'backorder',
    path: '/backorders',
    tabs: [
      { id: 'overview', perms: PERMS.auction },
      { id: 'drops', perms: PERMS.auction, client: true },
      { id: 'requests', perms: PERMS.auction, client: true },
      { id: 'catching', perms: PERMS.auction },
      { id: 'holds', perms: PERMS.billing },
    ],
  },
  {
    // "Đấu giá" module (reference 64–69): sessions, running them, bids, settlement and handover.
    // The former "Đấu giá" sub-tab of Tên miền.
    id: 'auctions',
    path: '/auctions',
    tabs: [
      { id: 'market', perms: PERMS.auction, client: true },
      { id: 'sessions', perms: ['auction.manage'] },
      { id: 'bids', perms: PERMS.auction },
      { id: 'settlement', perms: PERMS.auction },
      { id: 'handover', perms: PERMS.auction },
    ],
  },
  {
    id: 'compliance',
    path: '/compliance',
    tabs: [
      {
        id: 'vnnic',
        perms: PERMS.vnnic,
        subs: [
          { id: 'dossier-sync', legacy: ['/compliance/vnnic/dossier-sync'] },
          { id: 'international-reports', legacy: ['/compliance/vnnic/international-reports'] },
          { id: 'coverage', legacy: ['/compliance/vnnic/coverage'] },
          { id: 'corrections', legacy: ['/compliance/vnnic/corrections'] },
          { id: 'post-operations' },
          { id: 'registrar-signature' },
          { id: 'tracking-reports' },
        ],
      },
      {
        id: 'icann',
        perms: PERMS.icann,
        subs: [
          { id: 'rdap', legacy: ['/compliance/icann/rdap'] },
          { id: 'rde', legacy: ['/compliance/icann/rde'] },
          { id: 'disclosure', legacy: ['/compliance/icann/disclosure'] },
        ],
      },
      {
        id: 'cases',
        perms: PERMS.cases,
        subs: [
          { id: 'inbox', legacy: ['/compliance/cases'] },
          { id: 'abuse', legacy: ['/compliance/icann/abuse'] },
          { id: 'disputes', legacy: ['/compliance/icann/disputes'] },
          { id: 'legal-locks', legacy: ['/compliance/legal-locks'] },
        ],
      },
      { id: 'policies', legacy: ['/compliance/policies'], perms: PERMS.compliance },
    ],
  },
  {
    // "Registry & vận hành": the registries with their commands, poll messages and
    // reconciliation (the former registry hub) next to system health, so "why did this fail"
    // starts in one hub.
    id: 'ops',
    path: '/ops',
    tabs: [
      { id: 'overview', legacy: ['/ops/overview'], perms: PERMS.ops },
      {
        id: 'registry',
        perms: PERMS.registry,
        subs: [
          { id: 'registries', legacy: ['/registry/registries'] },
          { id: 'transactions', legacy: ['/registry/transactions'] },
          { id: 'reconciliation', legacy: ['/registry/reconciliation'] },
          { id: 'poll', legacy: ['/registry/poll'] },
          { id: 'sessions', legacy: ['/registry/sessions'] },
          { id: 'epp', legacy: ['/registry/epp'] },
          { id: 'health', legacy: ['/ops/registries'], perms: PERMS.ops },
        ],
      },
      {
        id: 'queues',
        perms: PERMS.ops,
        subs: [
          { id: 'queues', legacy: ['/ops/queues'] },
          { id: 'workflows', legacy: ['/ops/workflows'] },
        ],
      },
      {
        id: 'alerts',
        perms: PERMS.ops,
        subs: [
          { id: 'alerts', legacy: ['/ops/alerts'] },
          { id: 'incidents', legacy: ['/ops/incidents'] },
        ],
      },
      { id: 'services', legacy: ['/ops/services'], perms: PERMS.ops },
      {
        id: 'telemetry',
        perms: PERMS.ops,
        subs: [
          { id: 'metrics', legacy: ['/ops/metrics'] },
          { id: 'logs', legacy: ['/ops/logs'] },
          { id: 'traces', legacy: ['/ops/traces'] },
        ],
      },
      { id: 'slo', legacy: ['/ops/slo'], perms: PERMS.ops },
    ],
  },
  {
    id: 'settings',
    path: '/settings',
    tabs: [
      // "Tất cả cài đặt": one searchable page of every setting, grouped the way the owner thinks
      // (Bán tên miền, Kết nối, Tổ chức & nhân viên, Tuân thủ); each entry opens its one place.
      { id: 'all', perms: [...PERMS.integration, ...PERMS.pricing, ...PERMS.notify, ...PERMS.staff, ...PERMS.apiKeys, ...PERMS.compliance, ...PERMS.reporting] },
      {
        id: 'domains',
        perms: PERMS.integration,
        subs: [
          { id: 'channels', legacy: ['/settings/tlds'] },
          { id: 'lifecycle', legacy: ['/settings/tld-settings'] },
          { id: 'parking' },
          { id: 'requirements', legacy: ['/settings/name-change-requirements'] },
          { id: 'declarations' },
          { id: 'document-types', perms: ['dossier.read', 'dossier.review', 'compliance.read', 'compliance.override'] },
          { id: 'trust-anchors' },
          { id: 'declaration-checks' },
          { id: 'vn-zones' },
        ],
      },
      {
        id: 'connections',
        perms: [...PERMS.integration, ...PERMS.reporting],
        subs: [
          { id: 'registries', legacy: ['/settings/registries'], perms: PERMS.integration },
          { id: 'vnnic', legacy: ['/settings/vnnic'], perms: PERMS.integration },
          { id: 'smartca', perms: PERMS.integration },
          // Cloudflare Turnstile: the anti-robot check of the client sign-in forms.
          { id: 'captcha', perms: PERMS.integration },
          { id: 'ote', perms: PERMS.integration },
          // "API báo cáo": Twenty CRM and internal reports read business data (docs/specs/reporting-api.md §12).
          { id: 'reporting', perms: PERMS.reporting },
        ],
      },
      // "Module": the integration modules of every business branch (ADR-025 §8); `branch=` picks
      // the branch's tab, `module=` and `instance=` open one connection's page.
      { id: 'modules', perms: [...PERMS.integration, 'integration.read'] },
      {
        id: 'notify',
        perms: PERMS.notify,
        subs: [
          { id: 'email', legacy: ['/settings/email'] },
          { id: 'sent', legacy: ['/settings/email-log'] },
          // Zalo ZNS: which notice uses which Zalo template, messages sent, numbers that receive none.
          { id: 'zalo' },
          { id: 'webhooks', legacy: ['/settings/webhooks'] },
        ],
      },
      {
        id: 'security',
        perms: [...PERMS.staff, ...PERMS.apiKeys, ...PERMS.ops],
        subs: [
          { id: 'staff', legacy: ['/admin/staff'], perms: PERMS.staff },
          { id: 'roles', legacy: ['/admin/roles'], perms: PERMS.staff },
          { id: 'api-keys', legacy: ['/admin/api-credentials'], perms: PERMS.apiKeys },
          { id: 'audit', legacy: ['/admin/audit'], perms: PERMS.audit },
          // "Lưu trữ log": days each log class stays in Loki (ops.read reads, ops.admin changes).
          { id: 'logs', perms: PERMS.ops },
        ],
      },
    ],
  },
];

export function findHub(id: string): HubDef | undefined {
  return HUBS.find((h) => h.id === id);
}

/** `/domains?tab=transfers`, `/settings?tab=connections&sub=vnnic`; `extra` adds more parameters. */
export function hubUrl(hub: HubId, tab?: string, sub?: string, extra?: Record<string, string>): string {
  const h = findHub(hub);
  const p = new URLSearchParams();
  if (tab) p.set('tab', tab);
  if (tab && sub) p.set('sub', sub);
  for (const [k, v] of Object.entries(extra ?? {})) if (v) p.set(k, v);
  const qs = p.toString();
  return `${h?.path ?? '/'}${qs ? `?${qs}` : ''}`;
}

export interface HubLocation {
  hub: HubId;
  tab: string;
  sub?: string;
}

export interface LegacyRedirect extends HubLocation {
  from: string;
  to: string;
}

/** Every former list URL and the hub tab it now opens. */
export const LEGACY_REDIRECTS: readonly LegacyRedirect[] = HUBS.flatMap((hub) =>
  hub.tabs.flatMap((tab) => [
    ...(tab.legacy ?? []).map((from) => ({ from, hub: hub.id, tab: tab.id, to: hubUrl(hub.id, tab.id) })),
    ...(tab.subs ?? []).flatMap((sub) => (sub.legacy ?? []).map((from) => ({ from, hub: hub.id, tab: tab.id, sub: sub.id, to: hubUrl(hub.id, tab.id, sub.id) }))),
  ]),
);

/**
 * Where an old list URL goes, keeping its query: the page's own `tab` parameter becomes `pane`
 * because `tab` now names the hub tab. Returns null for a URL that is not a former list page.
 */
export function legacyTarget(pathname: string, search = ''): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const hit = LEGACY_REDIRECTS.find((r) => r.from === path);
  if (!hit) return null;
  const old = new URLSearchParams(search);
  const extra: Record<string, string> = {};
  for (const [k, v] of old) {
    if (k === 'tab') extra.pane = v;
    else if (k !== 'sub') extra[k] = v;
  }
  return hubUrl(hit.hub, hit.tab, hit.sub, extra);
}

/**
 * Hub tabs that moved to another hub (2026-09-29 layout review) or were a second copy of a page
 * that has one place now. `path` is the old hub's URL; `tab` / `sub` its old tab (no `tab`: the
 * removed hub's default and any tab not listed). Other query parameters are kept.
 */
export const MOVED_TABS: readonly { path: string; tab?: string; sub?: string; when?: readonly [param: string, value: string]; to: HubLocation }[] = [
  // The former "TLD & Registry" hub: TLDs are sold in "TLD & giá bán", registries run in "Registry & vận hành".
  { path: '/registry', tab: 'tlds', to: { hub: 'products', tab: 'tlds' } },
  { path: '/registry', tab: 'catalog', to: { hub: 'products', tab: 'catalog' } },
  { path: '/registry', tab: 'registries', to: { hub: 'ops', tab: 'registry', sub: 'registries' } },
  { path: '/registry', tab: 'epp', to: { hub: 'ops', tab: 'registry', sub: 'epp' } },
  { path: '/registry', tab: 'sessions', to: { hub: 'ops', tab: 'registry', sub: 'sessions' } },
  { path: '/registry', tab: 'transactions', to: { hub: 'ops', tab: 'registry', sub: 'transactions' } },
  { path: '/registry', tab: 'poll', to: { hub: 'ops', tab: 'registry', sub: 'poll' } },
  { path: '/registry', tab: 'reconciliation', to: { hub: 'ops', tab: 'registry', sub: 'reconciliation' } },
  { path: '/registry', to: { hub: 'products', tab: 'tlds' } },
  // Registry health sits with the registries; services became a single tab.
  { path: '/ops', tab: 'services', sub: 'registries', to: { hub: 'ops', tab: 'registry', sub: 'health' } },
  // Second copies: prices live in "TLD & giá bán", approvals in "Việc cần xử lý", the VNNIC connection in Cài đặt › Kết nối.
  { path: '/settings', tab: 'products', sub: 'services', to: { hub: 'products', tab: 'services' } },
  { path: '/settings', tab: 'products', sub: 'promotions', to: { hub: 'products', tab: 'promotions' } },
  { path: '/settings', tab: 'products', sub: 'tax', to: { hub: 'products', tab: 'tax' } },
  { path: '/settings', tab: 'products', to: { hub: 'products', tab: 'pricing' } },
  { path: '/settings', tab: 'security', sub: 'approvals', to: { hub: 'home', tab: 'approvals' } },
  { path: '/compliance', tab: 'vnnic', sub: 'connection', to: { hub: 'settings', tab: 'connections', sub: 'vnnic' } },
  // Backorders and auctions left Tên miền for modules of their own (reference 58–70).
  { path: '/domains', tab: 'market', sub: 'auctions', to: { hub: 'auctions', tab: 'market' } },
  { path: '/domains', tab: 'market', when: ['view', 'drops'], to: { hub: 'backorder', tab: 'drops' } },
  // The former backorder page's own switch (`/backorders?view=drops`).
  { path: '/backorders', when: ['view', 'drops'], to: { hub: 'backorder', tab: 'drops' } },
  { path: '/domains', tab: 'market', to: { hub: 'backorder', tab: 'requests' } },
];

/**
 * Where a hub URL whose tab moved goes now (query kept, `tab` / `sub` replaced); null when the
 * tab did not move. `/settings?tab=products&sub=costs` → `/products?tab=pricing`.
 */
export function movedTarget(pathname: string, search = ''): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const candidates = MOVED_TABS.filter((m) => m.path === path);
  if (!candidates.length) return null;
  const p = new URLSearchParams(search);
  const tab = p.get('tab') ?? undefined;
  const sub = p.get('sub') ?? undefined;
  const stillAHub = HUBS.some((h) => h.path === path);
  const hit =
    candidates.find((m) => m.tab === tab && m.when && p.get(m.when[0]) === m.when[1]) ??
    candidates.find((m) => m.tab === tab && !m.when && m.sub !== undefined && m.sub === sub) ??
    candidates.find((m) => m.tab === tab && !m.when && m.sub === undefined) ??
    // A removed hub sends every other tab to its default; a hub that still exists keeps them.
    (stillAHub ? undefined : candidates.find((m) => m.tab === undefined));
  if (!hit) return null;
  const extra: Record<string, string> = {};
  for (const [k, v] of p) if (k !== 'tab' && k !== 'sub' && k !== hit.when?.[0]) extra[k] = v;
  return hubUrl(hit.to.hub, hit.to.tab, hit.to.sub, extra);
}

/** Detail pages (object URLs) and the hub tab they belong to, for the sidebar and breadcrumbs. */
export const DETAIL_PREFIXES: readonly (HubLocation & { prefix: string })[] = [
  { prefix: '/domains/', hub: 'domains', tab: 'list' },
  { prefix: '/transfers/', hub: 'domains', tab: 'transfers' },
  { prefix: '/registrant-changes/', hub: 'domains', tab: 'registrant-changes' },
  { prefix: '/contacts/', hub: 'domains', tab: 'contacts' },
  { prefix: '/backorders/', hub: 'backorder', tab: 'requests' },
  { prefix: '/drops/', hub: 'backorder', tab: 'drops' },
  { prefix: '/auctions/', hub: 'auctions', tab: 'market' },
  { prefix: '/customers/', hub: 'customers', tab: 'customers' },
  { prefix: '/subjects/', hub: 'customers', tab: 'subjects' },
  { prefix: '/dossiers/', hub: 'customers', tab: 'dossiers' },
  { prefix: '/documents/', hub: 'customers', tab: 'documents' },
  { prefix: '/quotes/', hub: 'sales', tab: 'quotes' },
  { prefix: '/orders/', hub: 'sales', tab: 'orders' },
  { prefix: '/invoices/', hub: 'sales', tab: 'invoices' },
  { prefix: '/payments/', hub: 'sales', tab: 'payments' },
  { prefix: '/refunds/', hub: 'sales', tab: 'payments' },
  { prefix: '/bank-transactions/', hub: 'sales', tab: 'bank-transactions' },
  { prefix: '/ledger/', hub: 'sales', tab: 'ledger' },
  { prefix: '/holds/', hub: 'sales', tab: 'overview' },
  { prefix: '/pricing/', hub: 'products', tab: 'pricing' },
  { prefix: '/promotions/', hub: 'products', tab: 'promotions' },
  { prefix: '/registry/tlds/', hub: 'products', tab: 'tlds' },
  { prefix: '/registry/registries/', hub: 'ops', tab: 'registry', sub: 'registries' },
  { prefix: '/registry/transactions/', hub: 'ops', tab: 'registry', sub: 'transactions' },
  { prefix: '/registry/poll/', hub: 'ops', tab: 'registry', sub: 'poll' },
  { prefix: '/registry/reconciliation/', hub: 'ops', tab: 'registry', sub: 'reconciliation' },
  { prefix: '/compliance/work/', hub: 'home', tab: 'queue' },
  { prefix: '/name-changes/', hub: 'home', tab: 'requests' },
  { prefix: '/compliance/vnnic/sync-transactions/', hub: 'compliance', tab: 'vnnic', sub: 'dossier-sync' },
  { prefix: '/compliance/vnnic/international-reports/', hub: 'compliance', tab: 'vnnic', sub: 'international-reports' },
  { prefix: '/compliance/icann/rde/', hub: 'compliance', tab: 'icann', sub: 'rde' },
  { prefix: '/compliance/icann/disclosure/', hub: 'compliance', tab: 'icann', sub: 'disclosure' },
  { prefix: '/compliance/icann/abuse/', hub: 'compliance', tab: 'cases', sub: 'abuse' },
  { prefix: '/compliance/law-enforcement-requests/', hub: 'compliance', tab: 'cases', sub: 'abuse' },
  { prefix: '/compliance/icann/disputes/', hub: 'compliance', tab: 'cases', sub: 'disputes' },
  { prefix: '/compliance/legal-locks/', hub: 'compliance', tab: 'cases', sub: 'legal-locks' },
  { prefix: '/compliance/policies/', hub: 'compliance', tab: 'policies' },
  { prefix: '/ops/services/', hub: 'ops', tab: 'services' },
  { prefix: '/ops/workflows/', hub: 'ops', tab: 'queues', sub: 'workflows' },
  { prefix: '/ops/alerts/', hub: 'ops', tab: 'alerts', sub: 'alerts' },
  { prefix: '/ops/incidents/', hub: 'ops', tab: 'alerts', sub: 'incidents' },
  { prefix: '/ops/traces/', hub: 'ops', tab: 'telemetry', sub: 'traces' },
  { prefix: '/ops/correlate/', hub: 'ops', tab: 'telemetry', sub: 'traces' },
  { prefix: '/settings/registries/', hub: 'settings', tab: 'connections', sub: 'registries' },
  { prefix: '/admin/staff/', hub: 'settings', tab: 'security', sub: 'staff' },
  { prefix: '/admin/roles/', hub: 'settings', tab: 'security', sub: 'roles' },
];

/**
 * The hub tab a URL shows or belongs to: a hub page (its `tab` / `sub` parameters, or none for
 * the hub's default tab), a former list URL, or a detail page. Null for anything else.
 */
export function locate(pathname: string, search = ''): (HubLocation & { exact: boolean }) | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const moved = movedTarget(path, search);
  if (moved) return locate(moved.split('?')[0] ?? '', moved.split('?')[1] ?? '');
  const hub = HUBS.find((h) => h.path === path);
  if (hub) {
    const p = new URLSearchParams(search);
    const tab = hub.tabs.find((x) => x.id === p.get('tab')) ?? hub.tabs[0];
    if (!tab) return null;
    const sub = tab.subs?.find((s) => s.id === p.get('sub')) ?? tab.subs?.[0];
    return { hub: hub.id, tab: tab.id, sub: sub?.id, exact: true };
  }
  const legacy = LEGACY_REDIRECTS.find((r) => r.from === path);
  if (legacy) return { hub: legacy.hub, tab: legacy.tab, sub: legacy.sub, exact: true };
  let best: (typeof DETAIL_PREFIXES)[number] | undefined;
  for (const d of DETAIL_PREFIXES) if (`${path}/`.startsWith(d.prefix) && path.length >= d.prefix.length && (!best || d.prefix.length > best.prefix.length)) best = d;
  return best ? { hub: best.hub, tab: best.tab, sub: best.sub, exact: false } : null;
}
